<template>
  <AppShell title="Handpenningslån">
    <p v-if="isLoading" class="text-subtle">Hämtar handpenningslån...</p>

    <p v-else-if="notFound" class="text-subtle">
      Inget aktivt handpenningslån hittades. Lånet kan vara återbetalt eller inte längre aktivt.
    </p>

    <div v-else-if="depositLoan" class="stack">
      <DepositLoanHeader :deposit-loan="depositLoan" />
      <DepositLoanDetailsCard :deposit-loan="depositLoan" />
    </div>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { notify } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import { fetchDepositLoanById, DepositLoanRequestError } from '@/services/depositLoan'
import type { DepositLoan } from '@/services/depositLoan.types'
import DepositLoanHeader from './DepositLoanHeader.vue'
import DepositLoanDetailsCard from './components/DepositLoanDetailsCard.vue'

const route = useRoute()

const isLoading = ref(true)
const notFound = ref(false)
const depositLoan = ref<DepositLoan | null>(null)

const depositLoanId = computed(() => route.params.id.toString())

const loadDepositLoan = async () => {
  isLoading.value = true
  notFound.value = false
  depositLoan.value = null

  try {
    depositLoan.value = await fetchDepositLoanById(depositLoanId.value)
  } catch (err) {
    if (err instanceof DepositLoanRequestError && err.status === 404) {
      notFound.value = true
    } else {
      const message =
        err instanceof Error ? err.message : 'Ett fel uppstod när handpenningslånet hämtades.'
      notify({ type: 'error', title: message, dismissable: true })
    }
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  void loadDepositLoan()
})

watch(depositLoanId, () => {
  void loadDepositLoan()
})
</script>

<style scoped lang="scss">
.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
}
</style>
